import firebase from 'firebase/compat/app';
import { UserData } from '../types';

// Extend types to match expected Firebase data structure
export interface FirebaseUserData {
  address?: string;
  isAdmin?: boolean;
}

export interface FirebaseAddressData {
  rentAmount?: number;
  dueDate?: string;
  rentPaid?: boolean;
}

/**
 * Retrieves user data from Firebase and returns cleanup function
 */
export const setupUserDataListener = (
  user: firebase.User,
  setUserData: React.Dispatch<React.SetStateAction<UserData>>
): (() => void) => {
  const userInfoRef = firebase.database().ref(`users/${user.uid}`);
  let addressUnsubscribe: (() => void) | null = null;
  
  const userUnsubscribe = userInfoRef.on('value', (snapshot) => {
    const userFbData: FirebaseUserData | null = snapshot.val();
    
    if (!userFbData) return;

    setUserData((prevData) => ({
      ...prevData,
      userAddress: userFbData.address || null,
      isAdmin: userFbData.isAdmin || false
    }));

    // Clean up previous address listener
    if (addressUnsubscribe) {
      addressUnsubscribe();
      addressUnsubscribe = null;
    }

    if (userFbData.address) {
      const addressInfoRef = firebase.database().ref(`addresses/${userFbData.address}`);
      
      const addressListener = addressInfoRef.on('value', (snapshot) => {
        const addressFbData: FirebaseAddressData | null = snapshot.val();
        
        if (addressFbData) {
          setUserData((prevData) => ({
            ...prevData,
            rentAmount: addressFbData.rentAmount || null,
            dueDate: addressFbData.dueDate ? new Date(addressFbData.dueDate) : null,
            rentPaid: addressFbData.rentPaid || false
          }));
        }
      });

      addressUnsubscribe = () => {
        addressInfoRef.off('value', addressListener);
      };
    }
  });

  // Return cleanup function
  return () => {
    userInfoRef.off('value', userUnsubscribe);
    if (addressUnsubscribe) {
      addressUnsubscribe();
    }
  };
};

export const getAuthErrorMessage = (error: firebase.FirebaseError): string => {
  switch (error.code) {
    case 'auth/user-not-found':
      return 'No account found with this email address.';
    case 'auth/wrong-password':
      return 'Incorrect password. Please try again.';
    case 'auth/email-already-in-use':
      return 'An account with this email already exists.';
    case 'auth/weak-password':
      return 'Password is too weak. Please choose a stronger password.';
    case 'auth/invalid-email':
      return 'Please enter a valid email address.';
    case 'auth/user-disabled':
      return 'This account has been disabled.';
    case 'auth/too-many-requests':
      return 'Too many failed attempts. Please try again later.';
    case 'auth/network-request-failed':
      return 'Network error. Please check your connection and try again.';
    default:
      return error.message || 'An unexpected error occurred. Please try again.';
  }
};

export const isFirebaseAuthError = (error: unknown): error is firebase.FirebaseError => {
  return typeof error === 'object' && error !== null && 'code' in error;
};